/**
 * 라인 단위 diff — 결정 #30. AI 수리 결과를 적용 전에 사용자에게 보여주기 위한 순수
 * 함수다(결정 #10). "AI가 기능을 통째로 지워 검증만 통과시키는" 함정을 사용자가
 * 눈으로 잡아낼 수 있게 하는 안전장치다.
 *
 * 표준 LCS(최장 공통 부분수열) 기반의 최소 편집 표현. 거대 파일용 고성능 알고리즘은
 * 아니지만, 게임 정의·스크립트 크기(수백 줄)에는 충분하고 6s+에서도 가볍다.
 */

export type DiffOp = "equal" | "add" | "remove";

export interface DiffLine {
  op: DiffOp;
  /** 줄 내용(개행 제외). */
  text: string;
}

/**
 * 두 텍스트를 라인 단위로 비교해 편집 스크립트를 반환한다. equal/remove(old)/add(new).
 * UI는 remove는 빨강, add는 초록으로 그리면 된다.
 */
export function diffLines(oldText: string, newText: string): DiffLine[] {
  const a = oldText.split("\n");
  const b = newText.split("\n");
  const n = a.length;
  const m = b.length;

  // LCS 길이 테이블(동적 계획법). (n+1) x (m+1).
  const lcs: number[][] = Array.from({ length: n + 1 }, () => new Array<number>(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      lcs[i]![j] = a[i] === b[j] ? lcs[i + 1]![j + 1]! + 1 : Math.max(lcs[i + 1]![j]!, lcs[i]![j + 1]!);
    }
  }

  // 백트래킹으로 편집 스크립트 복원.
  const out: DiffLine[] = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      out.push({ op: "equal", text: a[i]! });
      i++;
      j++;
    } else if (lcs[i + 1]![j]! >= lcs[i]![j + 1]!) {
      out.push({ op: "remove", text: a[i]! });
      i++;
    } else {
      out.push({ op: "add", text: b[j]! });
      j++;
    }
  }
  while (i < n) {
    out.push({ op: "remove", text: a[i]! });
    i++;
  }
  while (j < m) {
    out.push({ op: "add", text: b[j]! });
    j++;
  }
  return out;
}

/** diff 요약 통계(추가/삭제 줄 수). UI 헤더용. */
export function diffStats(diff: DiffLine[]): { added: number; removed: number } {
  let added = 0;
  let removed = 0;
  for (const d of diff) {
    if (d.op === "add") added++;
    else if (d.op === "remove") removed++;
  }
  return { added, removed };
}
